import {
  Chip,
  formatNumber,
  Tooltip,
  TruncatedAccount,
} from "@narmi/design_system";
import Filters from "byzantine/src/filters";
import cc from "classcat";

import MerchantIcon from "../../../../components/subscriptions/MerchantIcon";

import CategoryCell from "./CategoryCell/CategoryCell";
import ReceiptCell from "./ReceiptCell";
import styles from "./ledgerColumns.module.scss";

import type { TransactionTableColumn } from "./TransactionTable";
import type { ReactLocalization } from "@fluent/react";
import type Account from "byzantine/src/Account";
import type { TransactionCategory } from "../hooks/useTransactionCategories";

export const transactionDescription = (transaction: API.Transaction) =>
  transaction.merchant ||
  transaction.description ||
  transaction.raw_description;

const findAccount = (accounts: Account[], accountId: API.AccountId) =>
  accounts.find((account) => account.id === accountId);

const DescriptionCell = ({ transaction }: { transaction: API.Transaction }) => {
  const description = transactionDescription(transaction);
  const subtext = transaction.merchant_description;
  const merchantMatched = transaction.merchant_matched;
  return (
    <div className={styles.description}>
      <MerchantIcon
        name={description}
        iconName={merchantMatched ? undefined : "dollar-sign"}
        size="s"
        variant={merchantMatched ? "muted" : "branded"}
      />
      <div className={styles.descriptionLines}>
        <span className={styles.descriptionText}>{description}</span>
        {subtext && (
          <span
            className={cc([
              "fontSize--xs",
              "fontColor--secondary",
              styles.descriptionSubtext,
            ])}
          >
            {subtext}
          </span>
        )}
      </div>
    </div>
  );
};

const DescriptionWithDateCell = ({
  transaction,
  l10n,
}: {
  transaction: API.Transaction;
  l10n: ReactLocalization;
}) => {
  const description = transactionDescription(transaction);
  const merchantMatched = transaction.merchant_matched;
  return (
    <div className={styles.description}>
      <MerchantIcon
        name={description}
        iconName={merchantMatched ? undefined : "dollar-sign"}
        size="s"
        variant={merchantMatched ? "muted" : "branded"}
      />
      <div className={styles.textStack}>
        <div className={styles.titleRow}>
          <div className={styles.descriptionText}>{description}</div>
          {!transaction.settled_at && (
            <Chip
              label={l10n.getString("live-ledger-status-pending")}
              kind="info"
            />
          )}
        </div>
        {transaction.settled_at && (
          <div className="fontSize--xs fontColor--secondary">
            {Filters.shortMonthDay(transaction.settled_at)}
          </div>
        )}
      </div>
    </div>
  );
};

const AccountCell = ({
  name,
  lastFour,
  organizationName,
}: {
  name: string;
  lastFour?: string;
  organizationName?: string;
}) => (
  <div className={styles.account}>
    <TruncatedAccount name={name} lastFour={lastFour} />
    {organizationName && (
      <div
        className={cc([
          "fontSize--xs",
          "fontColor--secondary",
          styles.accountOrganization,
        ])}
      >
        {organizationName}
      </div>
    )}
  </div>
);

export const BalanceCell = ({
  balance,
  className = "fontWeight--bold",
}: {
  balance: number | null;
  className?: string;
}) => {
  const value = Number(balance);
  if (balance === null || Number.isNaN(value)) {
    return null;
  }
  return (
    <span className={className} style={{ marginInlineStart: "auto" }}>
      {formatNumber(value / 100, "currency")}
    </span>
  );
};

const BalanceHeader = ({ l10n }: { l10n: ReactLocalization }) => (
  <span className={styles.balanceHeader}>
    <span>{l10n.getString("live-ledger-column-balance")}</span>
    <Tooltip
      text={l10n.getString("live-ledger-balance-tooltip")}
      side="left"
      maxWidth="130px"
    >
      <span
        className={cc([styles.balanceHeaderIcon, "narmi-icon-info"])}
        tabIndex={0}
        role="img"
        aria-label={l10n.getString("live-ledger-balance-tooltip")}
      />
    </Tooltip>
  </span>
);

export const AmountCell = ({ amount }: { amount: number }) => {
  const isCredit = amount > 0;
  return (
    <span
      className={cc(["fontWeight--bold", { "fontColor--success": isCredit }])}
      style={{ marginInlineStart: "auto" }}
    >
      {isCredit ? "+" : ""}
      {formatNumber(amount / 100, "currency")}
    </span>
  );
};

interface LedgerColumnDependencies {
  l10n: ReactLocalization;
  accounts: Account[];
  /** Picks the taxonomy for a row's own account, not the viewer's. */
  categoriesForTransaction: (
    transaction: API.Transaction
  ) => TransactionCategory[];
  canWriteTransactions: boolean;
  isMultiEntity?: boolean;
  /** The current user's own org name, from `currentUser.org_name`. The backend blanks
   * `Account.organization` for a user's own (non-affiliated) accounts, so this is the
   * only name available for the subtext on those rows. */
  organizationFallbackName?: string;
  receipts?: {
    canWrite: boolean;
    uploadingTransactionIds: ReadonlySet<string>;
    acceptMimeTypes?: string[];
    onFileChosen: (transaction: API.Transaction, file: File) => void;
    onPreview: (transaction: API.Transaction) => void;
  };
}

export const buildLedgerColumns = ({
  l10n,
  accounts,
  categoriesForTransaction,
  canWriteTransactions,
  isMultiEntity = false,
  organizationFallbackName,
  receipts,
}: LedgerColumnDependencies): TransactionTableColumn<API.Transaction>[] => {
  const columns: TransactionTableColumn<API.Transaction>[] = [
    {
      id: "date",
      header: l10n.getString("live-ledger-column-date"),
      cell: (transaction) =>
        transaction.settled_at ? (
          Filters.shortMonthDay(transaction.settled_at)
        ) : (
          <Chip
            label={l10n.getString("live-ledger-status-pending")}
            kind="info"
          />
        ),
      width: { s: "min-content", m: "0.5fr", l: "0.5fr" },
    },
    {
      id: "description",
      header: l10n.getString("live-ledger-column-description"),
      cell: (transaction) => <DescriptionCell transaction={transaction} />,
      width: { s: "1fr", m: "1.6fr", l: "1.6fr" },
    },
    {
      id: "descriptionWithDate",
      header: l10n.getString("live-ledger-column-description"),
      cell: (transaction) => (
        <DescriptionWithDateCell transaction={transaction} l10n={l10n} />
      ),
      width: { s: "1fr", m: "1.6fr", l: "1.6fr" },
    },
    {
      id: "account",
      header: l10n.getString("live-ledger-column-account"),
      cell: (transaction) => {
        const account = findAccount(accounts, transaction.account_id);
        if (!account) {
          return null;
        }
        const { name, endingValue } =
          account.getTruncatedValuesWithoutBalance();
        const organizationName = isMultiEntity
          ? account.organization?.name_dba ||
            account.organization?.name_legal ||
            organizationFallbackName
          : undefined;
        return (
          <AccountCell
            name={name}
            lastFour={endingValue}
            organizationName={organizationName || undefined}
          />
        );
      },
      width: { s: "1fr", m: "1fr", l: "1fr" },
    },
    {
      id: "category",
      header: l10n.getString("live-ledger-column-category"),
      minBreakpoint: "l",
      cell: (transaction) => (
        <CategoryCell
          transaction={transaction}
          categories={categoriesForTransaction(transaction)}
          canWrite={canWriteTransactions}
        />
      ),
      width: { s: "1fr", m: "1fr", l: "1fr" },
    },
  ];

  // Absent entirely when the FI does not have receipts_enabled.
  if (receipts) {
    columns.push({
      id: "receipt",
      header: l10n.getString("live-ledger-column-receipt"),
      minBreakpoint: "l",
      cell: (transaction) => (
        <ReceiptCell
          transaction={transaction}
          l10n={l10n}
          canWrite={receipts.canWrite}
          isUploading={receipts.uploadingTransactionIds.has(transaction.id)}
          acceptMimeTypes={receipts.acceptMimeTypes}
          onFileChosen={receipts.onFileChosen}
          onPreview={receipts.onPreview}
        />
      ),
      width: { s: "min-content", m: "min-content", l: "min-content" },
    });
  }

  columns.push({
    id: "amount",
    header: (
      <span style={{ marginInlineStart: "auto" }}>
        {l10n.getString("live-ledger-column-amount")}
      </span>
    ),
    textAlign: "end",
    cell: (transaction) => <AmountCell amount={transaction.amount} />,
    width: { s: "0.6fr", m: "0.6fr", l: "0.6fr" },
  });

  columns.push({
    id: "balance",
    header: <BalanceHeader l10n={l10n} />,
    textAlign: "end",
    cell: (transaction) => <BalanceCell balance={transaction.ledger_balance} />,
    width: { s: "0.5fr", m: "0.5fr", l: "0.5fr" },
  });

  return columns;
};

export const LedgerMobileRow = ({
  transaction,
  accounts,
  l10n,
  showBalance = false,
}: {
  transaction: API.Transaction;
  accounts: Account[];
  l10n: ReactLocalization;
  showBalance?: boolean;
}) => {
  const description = transactionDescription(transaction);
  const merchantMatched = transaction.merchant_matched;
  const account = findAccount(accounts, transaction.account_id);
  const meta = [
    transaction.settled_at
      ? Filters.shortMonthDay(transaction.settled_at)
      : null,
    account ? account.getName() : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className={styles.mobileRow}>
      <MerchantIcon
        name={description}
        iconName={merchantMatched ? undefined : "dollar-sign"}
        size="s"
        variant={merchantMatched ? "muted" : "branded"}
      />
      <div className={styles.textStack}>
        <div className={styles.titleRow}>
          <div className={styles.descriptionText}>{description}</div>
          {!transaction.settled_at && (
            <Chip
              label={l10n.getString("live-ledger-status-pending")}
              kind="info"
            />
          )}
        </div>
        <div className="fontSize--xs fontColor--secondary">{meta}</div>
      </div>
      <div className={styles.amountStack}>
        <AmountCell amount={transaction.amount} />
        {showBalance && (
          <BalanceCell
            balance={transaction.ledger_balance}
            className="fontSize--xs fontColor--secondary"
          />
        )}
      </div>
    </div>
  );
};
